import { observer } from "mobx-react-lite"
import styles from "./styles.module.scss"
import { PaymentPlanEnum, PaymentRequestID, TripID, UserID } from "@cruisen/contract"
import CloseModalButton from "../../buttons/CloseModalButton"
import { useStores } from "../../../models/helpers/useStores"
import { useQuery } from "@tanstack/react-query"
import { getPaymentRequest } from "../../../services/firebase-functions/billing"
import AlertCircleFill from "~icons/eva/alert-circle-fill"
import LinkOutline from "~icons/eva/link-2-outline"
import { DefaultSpinner } from "../../loaders/DefaultSpinner"
import { PaymentRequestDetailsCard } from "./PaymentDetailsCard"
import { formatCurrency } from "@cruisen/i18n"
import { paymentRequestStatus, paymentPlanStatus } from "@cruisen/react-shared"
import Config from "../../../config"
import { addTestCreditCardToPaymentRequest } from "../../../services/firestore/developerFunctions"
import { useEffect } from "react"
import * as Sentry from "@sentry/react"
import { Link } from "react-router-dom"
import { generateWebPreviewUrl } from "../../../services/firebase-functions/webPreview"

const ErrorState = ({
  paymentRequestId,
  tripId,
  loggedInUserId,
}: {
  paymentRequestId: PaymentRequestID
  tripId: TripID
  loggedInUserId: UserID
}) => {
  useEffect(() => {
    Sentry.captureMessage(`Quote details not found`, {
      extra: {
        tripId,
        paymentRequestId,
        loggedInUserId,
      },
    })
  }, [])

  return (
    <div className={styles.errorState} data-testid="payment-request-details-error">
      <AlertCircleFill className={styles.errorIcon} />
      <h2 className={styles.errorTitle}>Quote not found</h2>
      <p className={styles.errorMessage}>
        Oops! It seems that this quote doesn't work. Please try again later.
      </p>
    </div>
  )
}

export const PaymentRequestDetails = observer(function PaymentRequestDetails({
  paymentRequestId,
}: {
  paymentRequestId: PaymentRequestID
}) {
  const rootStore = useStores()
  const { activeTrip } = rootStore
  const closePaymentRequestDetails = () => {
    rootStore.setProp("paymentRequest", null)
  }

  const { data: paymentRequest, isLoading } = useQuery({
    queryKey: ["paymentRequest", paymentRequestId],
    queryFn: () => getPaymentRequest({ paymentRequestId }),
    staleTime: 0,
  })

  const assignedUserId = paymentRequest?.assignedTo?.[0]
  const assignedTo = activeTrip?.collaborators.filter((collaborator) =>
    paymentRequest?.assignedTo.includes(collaborator.userId),
  )
  const assignedToName = assignedTo
    ?.map((collaborator) => `${collaborator.firstName} ${collaborator.lastName}`)
    .join(", ")

  const room = activeTrip?.rooms.find((room) => room.id === paymentRequest?.lineItems[0].tripRoomId)

  const { data: webPreviewUrl, isLoading: isWebPreviewUrlLoading } = useQuery({
    queryKey: ["webPreviewUrl", assignedUserId, paymentRequestId],
    queryFn: () =>
      generateWebPreviewUrl({
        actionId: "openPaymentRequest",
        userId: assignedUserId,
        webPreviewData: {
          paymentRequest,
          tripId: activeTrip?.id,
          quoteName: paymentRequest?.label,
          tripSearchResultId: activeTrip?.chosenTripSearchResultId,
        },
      }),
    staleTime: 0,
    enabled: !!assignedUserId && !!paymentRequestId,
  })

  const paymentPlan = paymentRequest?.lineItems[0]?.paymentPlan
  const assignedItems = [
    {
      label: "Quote status",
      value: paymentRequestStatus.get(paymentRequest?.status)?.label,
      statusType: paymentRequestStatus.get(paymentRequest?.status)?.statusType,
      tooltip: "Here you can manually set a quote status. Everyone on the trip will see it.",
      testId: "payment-request-details-quote-status",
    },
    { label: "Assigned to", value: assignedToName },
    { label: "Room", value: room?.name },
    paymentPlan && {
      label: "Payment plan",
      value: paymentPlanStatus.get(paymentPlan),
    },
    { label: "Issued on", value: new Date(paymentRequest?.created).toLocaleDateString() },
  ].filter(Boolean)

  const depositItems = [
    {
      label: "Deposit amount",
      value: formatCurrency(Number(paymentRequest?.lineItems[0]?.deposit || 0), 2),
    },
    paymentPlan &&
      paymentPlan === PaymentPlanEnum.DepositOnly && {
        label: "Final due date",
        value: new Date(paymentRequest?.lineItems[0].totalDueDate).toLocaleDateString(),
      },
  ].filter(Boolean)

  return (
    <div className={styles.paymentRequestDetails} data-testid="payment-request-details">
      <div className={styles.paymentRequestDetailsHeading}>
        <h1 className={styles.paymentRequestDetailsTitle}>
          {isLoading ? "Loading..." : paymentRequest?.label || "Quote"}
        </h1>
        <div className={styles.paymentRequestDetailsHeadingButtons}>
          {Config.environment !== "production" && !isWebPreviewUrlLoading && webPreviewUrl && (
            <Link
              className={styles.paymentRequestDetailsHeadingLinkButton}
              to={webPreviewUrl?.generatedUrl}
              target="_blank"
            >
              <LinkOutline className={styles.linkIcon} />
            </Link>
          )}
          <CloseModalButton
            onClick={closePaymentRequestDetails}
            testId="payment-request-details-close-button"
          />
        </div>
      </div>
      <div className={styles.paymentRequestDetailsContent}>
        {isLoading ? (
          <div className={styles.loading}>
            <DefaultSpinner />
          </div>
        ) : !paymentRequest ? (
          <ErrorState
            paymentRequestId={paymentRequestId}
            tripId={activeTrip?.id}
            loggedInUserId={rootStore.loggedInUser?.id}
          />
        ) : (
          <div className={styles.list}>
            <PaymentRequestDetailsCard items={assignedItems} key="assignedItems" />
            <PaymentRequestDetailsCard items={depositItems} key="depositItems" />
            {paymentRequest.lineItems.map((lineItem, index) => (
              <PaymentRequestDetailsCard
                items={[
                  { label: "Cabin fare", value: formatCurrency(Number(lineItem.amount), 2) },
                  {
                    label: "Taxes & fees",
                    value: formatCurrency(Number(lineItem.taxesAndFees || 0), 2),
                  },
                ]}
                total={{ label: "Total", value: formatCurrency(Number(lineItem.total), 2) }}
                key={`lineItem-${index}`}
              />
            ))}
            {Config.environment === "development" ? (
              <button
                className={styles.devButton}
                data-testid="force-approve-payment-request-button"
                onClick={async () => {
                  await addTestCreditCardToPaymentRequest({
                    paymentRequest,
                  })
                }}
              >
                Force approve
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
})
